import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateVarianta } from "@/api/hooks/useVarianty";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import type { Varianta } from "@/api/schemas";
import { navrhNazvuCasti } from "@/lib/castiZakazky";

const NO_COPY = "__none__";

const schema = z.object({
  nazov: z.string().min(1, "Názov je povinný").max(50),
  popis: z.string().max(500).optional(),
});

type FormData = z.infer<typeof schema>;

/**
 * Čo okno pridáva (KOLO 208): výpočet (alternatíva ceny pre zákazníka;
 * zaškrtnutím výrobná = preklopenie výpočtu do výroby), prvú výrobnú
 * variantu, alebo ďalšiu **časť výroby** v zákazke, ktorá výrobu už má.
 * Výpočet časťou nie je nikdy (KOLO 205 to robilo — zrušené).
 */
export type RezimNovejVarianty = "vypocet" | "cast" | "vyrobna";

export interface VariantaCreateDialogProps {
  zakazkaId: string;
  variants: Varianta[];
  rezim?: RezimNovejVarianty;
  /** Názov zákazky — predvolený názov doterajšieho výpočtu, keď sa stane časťou. */
  zakazkaNazov?: string;
  /** Malá zákazka bez AA (KOLO 200) — do výroby ide so svojím Z, AA nedostane. */
  cisloZ?: string | null;
  /** Zákazka je výpočet V — prvá výroba potrebuje číslo AA. Zákazka s AA
   *  (Doplniť AA, import) ho už má. */
  jeVypocet?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VariantaCreateDialog({
  zakazkaId,
  variants,
  rezim = "vypocet",
  zakazkaNazov = "",
  cisloZ = null,
  jeVypocet = true,
  open,
  onOpenChange,
}: VariantaCreateDialogProps): JSX.Element {
  const createMutation = useCreateVarianta(zakazkaId);
  const [copyFrom, setCopyFrom] = useState<string>(NO_COPY);
  const [jeVyrobnaZaskrtnuta, setJeVyrobna] = useState(false);
  const [vyrobneCislo, setVyrobneCislo] = useState("");

  const maVyrobnu = variants.some((v) => v.je_vyrobna);
  const novaCast = rezim === "cast";
  const jeVyrobna =
    rezim === "vyrobna" ||
    novaCast ||
    (rezim === "vypocet" && !maVyrobnu && jeVyrobnaZaskrtnuta);
  // Jediná doterajšia výroba mimo častí sa stane prvou časťou — dostane meno
  const mimoCasti = variants.filter((v) => v.je_vyrobna && !v.je_cast);
  const doterajsi = novaCast && mimoCasti.length === 1 ? mimoCasti[0] : undefined;
  const predvolenyDoterajsi = doterajsi
    ? doterajsi.urceni_casti
      ? navrhNazvuCasti(doterajsi)
      : zakazkaNazov.trim().slice(0, 50) || doterajsi.nazov
    : "";
  const [doterajsiNazov, setDoterajsiNazov] = useState(predvolenyDoterajsi);
  // AA sa pýta len pri prvej výrobe výpočtu V, nikdy pri malej zákazke Z
  const pytaAa = jeVyrobna && !maVyrobnu && !cisloZ && jeVypocet;
  const cenove = variants.filter((v) => !v.je_vyrobna);

  // Časť sa volá podľa toho, čo je (Obývačka), nie A, B
  const predvolenyNazov =
    rezim === "cast"
      ? ""
      : rezim === "vyrobna"
        ? suggestVyrobnaName(variants)
        : suggestNextName(variants);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { nazov: predvolenyNazov, popis: "" },
  });

  // Polia sa nastavia pri otvorení — okno je v editore pripojené stále
  // a režim (výpočet / časť / výrobná) sa mení tesne pred otvorením;
  // predvolené hodnoty useForm by ostali z prvého zobrazenia („B" pri časti)
  useEffect(() => {
    if (open) {
      reset({ nazov: predvolenyNazov, popis: "" });
      setDoterajsiNazov(predvolenyDoterajsi);
    } else {
      setCopyFrom(NO_COPY);
      setJeVyrobna(false);
      setVyrobneCislo("");
    }
    // Len pri otvorení / zatvorení — obnova variant počas písania by
    // prepísala rozpísaný názov
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function toggleVyrobna(checked: boolean): void {
    setJeVyrobna(checked);
    setValue(
      "nazov",
      checked ? suggestVyrobnaName(variants) : suggestNextName(variants),
    );
  }

  async function onSubmit(values: FormData): Promise<void> {
    if (pytaAa && !vyrobneCislo.trim()) {
      toast({
        variant: "destructive",
        title: "Chýba výrobné číslo",
        description: "Prvá výrobná varianta vyžaduje výrobné (AA) číslo.",
      });
      return;
    }
    if (doterajsi && !doterajsiNazov.trim()) {
      toast({
        variant: "destructive",
        title: "Chýba názov",
        description: `Zadaj, ako sa bude volať časť z výroby ${doterajsi.nazov}.`,
      });
      return;
    }
    try {
      await createMutation.mutateAsync(
        novaCast
          ? {
              nazov: values.nazov,
              popis: values.popis?.trim() || null,
              // Zisk, DPH, zľavu a réžiu preberie časť z prvej časti výroby
              // — výroba jednej zákazky sa počíta rovnako (KOLO 208)
              je_vyrobna: true,
              doterajsia_cast_nazov: doterajsi ? doterajsiNazov.trim() : null,
            }
          : {
              nazov: values.nazov,
              popis: values.popis?.trim() || null,
              je_vyrobna: jeVyrobna,
              vyrobne_cislo: pytaAa ? vyrobneCislo.trim() : null,
              zisk_pct: "10.00",
              // DPH sa neposiela — backend doplní predvoľbu z Nastavení
              // (KOLO 101). Natvrdo "20.00" tu nastavenie ticho obchádzalo.
              sleva_pct: "0.00",
              copy_from_variant_id: copyFrom !== NO_COPY ? copyFrom : null,
            },
      );
      toast({
        title: novaCast
          ? "Časť výroby pridaná"
          : jeVyrobna
            ? "Výrobná varianta vytvorená"
            : "Výpočet vytvorený",
        description: novaCast
          ? `Časť ${values.nazov} pridaná — výroba spolu je súčet častí.`
          : jeVyrobna
            ? `${values.nazov} pridaná do zákazky.`
            : `Výpočet ${values.nazov} pridaný do zákazky.`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Nepodarilo sa vytvoriť",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {novaCast
              ? "Pridať časť výroby"
              : jeVyrobna
                ? "Pridať výrobnú variantu"
                : "Pridať výpočet"}
          </DialogTitle>
          <DialogDescription>
            {novaCast
              ? "Ďalšia časť výroby — na pílu ide samostatne, výroba spolu je súčet častí. Výpočty sa nemenia."
              : rezim === "vyrobna"
                ? "Realita výroby — výrobná kalkulácia, cenu pre zákazníka (výpočet) nemení."
                : "Výpočty (A, B, C…) sú ceny pre zákazníka — alternatívy. Voliteľne skopíruj obsah iného výpočtu."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {doterajsi ? (
            <div className="space-y-2">
              <Label htmlFor="varianta-doterajsi">
                Doterajšia výroba {doterajsi.nazov} bude časť *
              </Label>
              <Input
                id="varianta-doterajsi"
                maxLength={50}
                value={doterajsiNazov}
                onChange={(e) => setDoterajsiNazov(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                Jeho dielce ostanú. Názov = dovetok z Určení (AA6262_
                <span className="font-medium">skrinka</span> → Skrinka) — podľa
                neho ďalší import z Tandem Zostáv trafí správnu časť.
              </p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="varianta-nazov">
              {rezim === "cast" ? "Názov novej časti *" : "Názov *"}
            </Label>
            <Input
              id="varianta-nazov"
              autoFocus
              placeholder={rezim === "cast" ? "napr. Obývačka" : undefined}
              {...register("nazov")}
            />
            {errors.nazov ? (
              <p className="text-xs text-destructive">{errors.nazov.message}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="varianta-popis">Popis (voliteľné)</Label>
            <Input
              id="varianta-popis"
              placeholder="napr. zmena farby dvierok"
              {...register("popis")}
            />
          </div>

          {rezim === "vypocet" && !maVyrobnu ? (
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={jeVyrobna}
                  onChange={(e) => toggleVyrobna(e.target.checked)}
                  aria-label="Výrobná varianta"
                  className="h-4 w-4 accent-primary"
                />
                <span>Výrobná varianta</span>
              </label>
              <p className="text-[11px] text-muted-foreground">
                Preklopenie výpočtu do výroby — výrobná kalkulácia, cenu pre
                zákazníka nemení. Výrobu z TopSolidu pripojíš importom.
              </p>
            </div>
          ) : null}

          {jeVyrobna && !maVyrobnu && cisloZ ? (
            <p className="text-[11px] text-muted-foreground">
              Malá zákazka bez AA — do výroby ide so svojím číslom {cisloZ}.
            </p>
          ) : null}
          {pytaAa ? (
            <div className="space-y-2">
              <Label htmlFor="varianta-vyrobne-cislo">
                Výrobné číslo (AA) *
              </Label>
              <Input
                id="varianta-vyrobne-cislo"
                placeholder="napr. AA6025"
                value={vyrobneCislo}
                onChange={(e) => setVyrobneCislo(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                Zákazka prejde do výroby — AA sa stane hlavným číslom, číslo
                výpočtu (V) ostane ako sekundárne.
              </p>
            </div>
          ) : null}

          {rezim !== "cast" ? (
            <div className="space-y-2">
              <Label htmlFor="copy-from">Kopírovať z</Label>
              <Select value={copyFrom} onValueChange={setCopyFrom}>
                <SelectTrigger
                  id="copy-from"
                  aria-label="Kopírovať zo zdrojového výpočtu"
                >
                  <SelectValue placeholder="(žiadne kopírovanie)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_COPY}>
                    (žiadne — prázdny výpočet)
                  </SelectItem>
                  {cenove.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.nazov}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Zrušiť
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Vytvára sa…" : "Pridať"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function suggestVyrobnaName(variants: Varianta[]): string {
  const used = new Set(variants.map((v) => v.nazov));
  if (!used.has("Výrobná")) return "Výrobná";
  let n = 2;
  while (used.has(`Výrobná ${n}`)) n++;
  return `Výrobná ${n}`;
}

function suggestNextName(variants: Varianta[]): string {
  const used = new Set(variants.map((v) => v.nazov));
  for (let i = 0; i < 26; i++) {
    const letter = String.fromCharCode(65 + i);
    if (!used.has(letter)) return letter;
  }
  let n = 27;
  while (used.has(`V${n}`)) n++;
  return `V${n}`;
}
